import { Reducer } from 'redux';
import { cloneDeep } from 'lodash-es';
import dayjs from 'dayjs';
import { TemplateScope, PreviewPageType } from '@dev/ee-api/ee-api-models';
import { EditorTemplateStatus } from '@dev/template-editor';
import { decodeFormMetaData } from '@/components/FormCreator/tools/decodeFormMetaData';
import {
    TemplateSaveStartAction,
    TemplateSaveErrorAction,
    TemplateLoadSuccessAction,
    SetDefaultTemplateAction,
    EditorMetaData,
    FormCreatorState,
    TemplateUploadFileSuccessAction,
    FormCreatorActionTypes,
    SetIsTemplateOpenAction,
    TemplateSaveSuccessAction,
    OpenUpsellingOverlayAction,
    ModifyFormAction,
    FormCreatorStatus,
    LoadSuccessAction,
    UpdateContactListsAction,
    SaveSuccessAction,
    StartEditStepAction,
    AddContactListErrorStatusAction,
    LoadPageTemplateSuccessAction,
    SetFormErrorsAction,
    FormCreatorTemplate,
    SetPopupDelayAction,
    UpdateSaveSettingsAction,
    SetPopupTriggerAction,
    SetTemplatePreviewAction
} from './types';

const generateNewTemplateName = (name?: string): string =>
    `${name ?? 'Form template'}: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`;

const getDefaultTemplateState = () => ({
    name: generateNewTemplateName(),
    status: EditorTemplateStatus.DEFAULT,
    isNew: true,
    files: {
        filesList: [],
        isFileUploading: false,
    },
    autosaveCounter: 0,
    recentColors: [],
});

const defaultState: FormCreatorState = {
    status: FormCreatorStatus.DEFAULT,
    isTemplateOpen: false,
    isTemplateDeselected: false,
    template: getDefaultTemplateState(),
    previousTemplate: null,
    openUpsellingFeature: null,
    contactLists: [],
    templatePreview: {
        content: null,
        name: null,
        isPublic: null,
    },
    isGalleryOpen: false,
    formEditor: {
        isOpen: false,
        sourceTemplateName: null,
    },
    popupDelay: null,
    saveSettings: {
        isPublic: null,
        customScreenshot: null,
    },
};

const loadStart = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    status: FormCreatorStatus.LOADING
});

const loadSuccess = (state: FormCreatorState, action: LoadSuccessAction): FormCreatorState => {
    const { formObject } = action.payload;
    const metaData = decodeFormMetaData(formObject?.MetaData);

    return {
        ...state,
        metaData,
        popupTrigger: metaData?.data?.popupTrigger ?? null,
        popupDelay: metaData?.data?.popupDelay ?? null,
        status: FormCreatorStatus.LOADED,
        formObject,
        contactLists: formObject?.Audience?.map((item) => item.PublicListID)
    };
}

const loadError = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    status: FormCreatorStatus.LOAD_ERROR
});

const saveStart = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    status: FormCreatorStatus.SAVING
});

const saveSuccess = (state: FormCreatorState, action: SaveSuccessAction): FormCreatorState => ({
    ...state,
    status: FormCreatorStatus.SAVED,
    formObject: action.payload.formObject,
    formPayload: null,
    stepInEdit: null,
    errors: null,
    metaData: decodeFormMetaData(action.payload.formObject?.MetaData)
});

const saveError = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    status: FormCreatorStatus.SAVE_ERROR
});

const pageTemplateSuccess = (state: FormCreatorState, action: LoadPageTemplateSuccessAction): FormCreatorState => {
    const { pageTemplateType, pageTemplate } = action.payload;
    const newParseTemplates = {
        ...(pageTemplateType === PreviewPageType.ActivationViaEmail && {activationViaEmailPage: pageTemplate}),
        ...(pageTemplateType === PreviewPageType.AlreadySubscribed && {alreadySubscribedPage: pageTemplate}),
        ...(pageTemplateType === PreviewPageType.FormSubmit && {formSubmitPage: pageTemplate}),
        ...(pageTemplateType === PreviewPageType.FormSubmitDoubleOptIn && {formSubmitDoubleOptInPage: pageTemplate}),
    }

    return {
        ...state,
        pageTemplates: {
            ...state.pageTemplates,
            ...newParseTemplates
        }
    }
};

const setFormErrors = (state: FormCreatorState, action: SetFormErrorsAction): FormCreatorState => ({
    ...state,
    errors: action.payload.errors,
    status: FormCreatorStatus.SAVE_ERROR
});

const modifyForm = (state: FormCreatorState, action: ModifyFormAction): FormCreatorState => ({
    ...state,
    formPayload: cloneDeep({
        ...state.formPayload,
        ...action.payload.formPayload
    })
});

const startEditStep = (state: FormCreatorState, action: StartEditStepAction): FormCreatorState => ({
    ...state,
    stepInEdit: action.payload.stepInEdit,
    formPayload: null,
});

const cancelEditStep = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    formPayload: null,
    stepInEdit: null,
    isTemplateDeselected: false,
    popupTrigger: state.metaData?.data?.popupTrigger,
    popupDelay: state.metaData?.data?.popupDelay,
});

const templateUploadFileStart = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    template: {
        ...state.template,
        files: {
            ...state.template.files,
            isFileUploading: true,
        }
    }
});

const templateUploadFileSuccess = (
    state: FormCreatorState,
    action: TemplateUploadFileSuccessAction
): FormCreatorState => ({
    ...state,
    template: {
        ...state.template,
        files: {
            filesList: action.payload.filesList,
            isFileUploading: false,
        }
    },
});

const templateUploadFileError = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    template: {
        ...state.template,
        files: {
            ...state.template.files,
            isFileUploading: false,
        },
    }
});


const setDefaultTemplate = (state: FormCreatorState, action: SetDefaultTemplateAction): FormCreatorState => {
    const { templateObject } = action.payload;
    const templateName = generateNewTemplateName();

    const metaData: EditorMetaData | null = templateObject?.EditorMetaData
        ? JSON.parse(Buffer.from(templateObject.EditorMetaData, 'base64').toString('utf8'))
        : null;

    return {
        ...state,
        previousTemplate: { ...state.template, status: EditorTemplateStatus.LOADED },
        template: {
            ...state.template,
            loadedJson: metaData?.data?.contentJson,
            isNew: true,
            status: EditorTemplateStatus.LOADED,
            name: templateName,
        },
        isTemplateOpen: true
    };
};

const templateIncrementAutosaveCounter = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    template: {
        ...state.template,
        autosaveCounter: 1 + state.template.autosaveCounter,
    }
});

const templateChanged = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    template: {
        ...state.template,
        status: EditorTemplateStatus.MODIFIED,
    }
});

const templateDeselect = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    isTemplateDeselected: true,
});

const templateSaveStart = (state: FormCreatorState, action: TemplateSaveStartAction): FormCreatorState => {
    const { errors, data } = action.payload;
    const { recentColors } = data;
    return {
        ...state,
        template: {
            ...state.template,
            status: EditorTemplateStatus.SAVING,
            nameError: null,
            saveErrors: errors,
            recentColors,
        }
    };
};

const templateSaveSuccess = (state: FormCreatorState, action: TemplateSaveSuccessAction): FormCreatorState => ({
    ...state,
    template: {
        ...state.template,
        isNew: false,
        status: EditorTemplateStatus.SAVED,
        loadedJson: action.payload.data.contentJson,
        recentColors: action.payload.data.recentColors
    },
    isTemplateDeselected: false,
    previousTemplate: null
});

const templateSaveError = (state: FormCreatorState, action: TemplateSaveErrorAction): FormCreatorState => ({
    ...state,
    template: {
        ...state.template,
        status: EditorTemplateStatus.SAVE_ERROR,
        nameError: action.payload.nameError,
    }
});

const templateLoadStart = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    template: {
        ...state.template,
        status: EditorTemplateStatus.LOADING,
    }
});

const templateLoadSuccess = (state: FormCreatorState, action: TemplateLoadSuccessAction): FormCreatorState => {
    const { templateObject, useDraftScope } = action.payload;
    const forceDraftScope = useDraftScope && templateObject.TemplateScope !== TemplateScope.Draft;
    const templateName =
        templateObject.IsPublic || forceDraftScope ? generateNewTemplateName(templateObject.Name) : templateObject.Name;
    const metaData: EditorMetaData | null = templateObject.EditorMetaData
        ? JSON.parse(Buffer.from(templateObject.EditorMetaData, 'base64').toString('utf8'))
        : null;
    
    const template: FormCreatorTemplate = {
        ...state.template,
        name: templateName,
        loadedJson: (metaData as EditorMetaData)?.data?.contentJson ?? null,
        recentColors: (metaData as EditorMetaData)?.data?.recentColors ?? null,
        isNew: templateObject.IsPublic || forceDraftScope,
        status: EditorTemplateStatus.LOADED,
    };
    if (action.payload.isPublic) {
        return {
            ...state,
            previousTemplate: { ...state.template, status: EditorTemplateStatus.LOADED },
            template
        };
    }

    return {
        ...state,
        template,
        saveSettings: {
            ...state.saveSettings,
            isPublic: templateObject.TemplateScope === TemplateScope.Public || state.saveSettings.isPublic,
        }
    };
};

const templateLoadError = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    template: {
        ...state.template,
        status: EditorTemplateStatus.LOAD_ERROR,
    }
});

const setIsTemplateOpen = (state: FormCreatorState, action: SetIsTemplateOpenAction): FormCreatorState => {
    if (action.payload.isOpen) {
        return {
            ...state,
            isTemplateOpen: true
        }
    }
    
    return {
        ...state,
        isTemplateOpen: false,
        template: state.previousTemplate ?? state.template, //restore template if default or from gallery closed unchanged
        previousTemplate: null,
    };
}

const openUpsellingOverlay = (state: FormCreatorState, action: OpenUpsellingOverlayAction): FormCreatorState => ({
    ...state,
    openUpsellingFeature: action.payload.feature,
});

const setTemplatePreview = (state: FormCreatorState, action: SetTemplatePreviewAction): FormCreatorState => ({
    ...state,
    templatePreview: {
        content: action.payload.content,
        name: action.payload.name,
        isPublic: action.payload.isPublic,
    }
});

const closeUpsellingOverlay = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    openUpsellingFeature: null,
});

const updateContactLists = (state: FormCreatorState, action: UpdateContactListsAction): FormCreatorState => ({
    ...state,
    contactLists: action.payload.contactLists
});

const addContactListErrorStatusChange = (state: FormCreatorState, action: AddContactListErrorStatusAction): FormCreatorState => ({
    ...state,
    isAddListError: action.payload.isAddListError
});

const openGallery = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    isGalleryOpen: true,
});

const closeGallery = (state: FormCreatorState): FormCreatorState => ({
    ...state,
    isGalleryOpen: false,
});

const setPopupDelay = (state: FormCreatorState, action: SetPopupDelayAction): FormCreatorState => ({
    ...state,
    popupDelay: action.payload.popupDelay,
});

const setPopupTrigger = (state: FormCreatorState, action: SetPopupTriggerAction): FormCreatorState => ({
    ...state,
    popupTrigger: action.payload.popupTrigger,
});

const updateSaveSettings = (state: FormCreatorState, action: UpdateSaveSettingsAction): FormCreatorState => ({
    ...state,
    saveSettings: {
        ...state.saveSettings,
        ...action.payload
    },
});

const reducer: Reducer<FormCreatorState> = (
    state: FormCreatorState = defaultState,
    action
): FormCreatorState => {
    switch (action.type) { // eslint-disable-line sonarjs/max-switch-cases
        case FormCreatorActionTypes.LOAD_START:
            return loadStart(state);
        case FormCreatorActionTypes.LOAD_SUCCESS:
            return loadSuccess(state, action as LoadSuccessAction);
        case FormCreatorActionTypes.LOAD_ERROR:
            return loadError(state);
        case FormCreatorActionTypes.SAVE_START:
            return saveStart(state);
        case FormCreatorActionTypes.SAVE_SUCCESS:
            return saveSuccess(state, action as SaveSuccessAction);
        case FormCreatorActionTypes.SAVE_ERROR:
            return saveError(state);
        case FormCreatorActionTypes.LOAD_PAGE_TEMPLATE_SUCCESS:
            return pageTemplateSuccess(state, action as LoadPageTemplateSuccessAction);
        case FormCreatorActionTypes.SET_FORM_ERRORS:
            return setFormErrors(state, action as SetFormErrorsAction);
        case FormCreatorActionTypes.MODIFY_FORM:
            return modifyForm(state, action as ModifyFormAction);
        case FormCreatorActionTypes.START_EDIT_STEP:
            return startEditStep(state, action as StartEditStepAction);
        case FormCreatorActionTypes.CANCEL_EDIT_STEP:
            return cancelEditStep(state);
        case FormCreatorActionTypes.TEMPLATE_UPLOAD_FILE_START:
            return templateUploadFileStart(state);
        case FormCreatorActionTypes.TEMPLATE_UPLOAD_FILE_SUCCESS:
            return templateUploadFileSuccess(state, action as TemplateUploadFileSuccessAction);
        case FormCreatorActionTypes.TEMPLATE_UPLOAD_FILE_ERROR:
            return templateUploadFileError(state);
        case FormCreatorActionTypes.SET_DEFAULT_TEMPLATE:
            return setDefaultTemplate(state, action as SetDefaultTemplateAction);
        case FormCreatorActionTypes.TEMPLATE_INCREMENT_AUTOSAVE_COUNTER:
            return templateIncrementAutosaveCounter(state);
        case FormCreatorActionTypes.TEMPLATE_CHANGED:
            return templateChanged(state);
        case FormCreatorActionTypes.TEMPLATE_DESELECT:
            return templateDeselect(state);
        case FormCreatorActionTypes.TEMPLATE_SAVE_START:
            return templateSaveStart(state, action as TemplateSaveStartAction);
        case FormCreatorActionTypes.TEMPLATE_SAVE_SUCCESS:
            return templateSaveSuccess(state, action as TemplateSaveSuccessAction);
        case FormCreatorActionTypes.TEMPLATE_SAVE_ERROR:
            return templateSaveError(state, action as TemplateSaveErrorAction);
        case FormCreatorActionTypes.TEMPLATE_LOAD_START:
            return templateLoadStart(state);
        case FormCreatorActionTypes.TEMPLATE_LOAD_SUCCESS:
            return templateLoadSuccess(state, action as TemplateLoadSuccessAction);
        case FormCreatorActionTypes.TEMPLATE_LOAD_ERROR:
            return templateLoadError(state);
        case FormCreatorActionTypes.SET_IS_TEMPLATE_OPEN:
            return setIsTemplateOpen(state, action as SetIsTemplateOpenAction);
        case FormCreatorActionTypes.SET_TEMPLATE_PREVIEW:
            return setTemplatePreview(state, action as SetTemplatePreviewAction);
        case FormCreatorActionTypes.OPEN_UPSELLING_OVERLAY:
            return openUpsellingOverlay(state, action as OpenUpsellingOverlayAction);
        case FormCreatorActionTypes.CLOSE_UPSELLING_OVERLAY:
            return closeUpsellingOverlay(state);
        case FormCreatorActionTypes.UPDATE_CONTACT_LISTS:
            return updateContactLists(state, action as UpdateContactListsAction);
        case FormCreatorActionTypes.ADD_CONTACT_LIST_ERROR_STATUS:
            return addContactListErrorStatusChange(state, action as AddContactListErrorStatusAction);
        case FormCreatorActionTypes.SET_POPUP_DELAY:
            return setPopupDelay(state, action as SetPopupDelayAction);
        case FormCreatorActionTypes.SET_POPUP_TRIGGER:
            return setPopupTrigger(state, action as SetPopupTriggerAction);
        case FormCreatorActionTypes.UPDATE_SAVE_SETTINGS:
            return updateSaveSettings(state, action as UpdateSaveSettingsAction);
        case FormCreatorActionTypes.OPEN_GALLERY:
            return openGallery(state);
        case FormCreatorActionTypes.CLOSE_GALLERY:
            return closeGallery(state);
        case FormCreatorActionTypes.RESET:
            return defaultState;
        default:
            return state;
    }
};

export { reducer as formCreator };

export default reducer;
